import { IFRAME_WIDGETS } from '../../config';

const TEXT_TYPES = ['i-text', 'textbox', 'text'];
const NAME_LENGTH = 40;

export type LayerPreview =
  | { kind: 'image'; src: string }
  | { kind: 'text'; fontFamily?: string; color?: string }
  | { kind: 'swatch'; color: string; round: boolean }
  | {
      kind: 'icon';
      icon: 'clock' | 'date' | 'web' | 'group' | 'shape' | 'qr';
    };

// Widgets are HTML laid over the canvas, so they always show in front of
// every other object whatever their canvas order.
export const isWidget = (object: fabric.Object) =>
  Object.values(IFRAME_WIDGETS).includes(object.data?.type);

export const getLayerName = (object: fabric.Object): string => {
  if (object.data?.name) return object.data.name;
  switch (object.data?.type) {
    case IFRAME_WIDGETS.WORLD_CLOCK:
      return 'Clock';
    case IFRAME_WIDGETS.DATE:
      return 'Date';
    case IFRAME_WIDGETS.IFRAME:
      return 'Web widget';
    case 'video':
      return 'Video';
    case 'qr':
      return `QR · ${object.data.qrContent || ''}`;
  }
  if (TEXT_TYPES.includes(object.type || '')) {
    const text = ((object as fabric.IText).text || '').split('\n')[0].trim();
    if (!text) return 'Text';
    return text.length > NAME_LENGTH ? `${text.slice(0, NAME_LENGTH)}…` : text;
  }
  switch (object.type) {
    case 'image':
      return 'Image';
    case 'group':
      return `Group · ${(object as fabric.Group).size()}`;
    case 'rect':
      return 'Rectangle';
    case 'circle':
      return 'Circle';
    case 'ellipse':
      return 'Ellipse';
    case 'triangle':
      return 'Triangle';
    case 'line':
      return 'Line';
    default:
      return 'Shape';
  }
};

export const getLayerPreview = (object: fabric.Object): LayerPreview => {
  switch (object.data?.type) {
    case IFRAME_WIDGETS.WORLD_CLOCK:
      return { kind: 'icon', icon: 'clock' };
    case IFRAME_WIDGETS.DATE:
      return { kind: 'icon', icon: 'date' };
    case IFRAME_WIDGETS.IFRAME:
      return { kind: 'icon', icon: 'web' };
    case 'qr':
      return { kind: 'icon', icon: 'qr' };
    case 'video':
      return object.data.thumbnailUrl
        ? { kind: 'image', src: object.data.thumbnailUrl }
        : { kind: 'icon', icon: 'shape' };
  }
  if (TEXT_TYPES.includes(object.type || '')) {
    const text = object as fabric.IText;
    return {
      kind: 'text',
      fontFamily: text.fontFamily,
      color: typeof text.fill === 'string' ? text.fill : undefined,
    };
  }
  if (object.type === 'image') {
    const src = (object as fabric.Image).getSrc?.();
    if (src) return { kind: 'image', src };
  }
  if (object.type === 'group') return { kind: 'icon', icon: 'group' };
  if (typeof object.fill === 'string' && object.fill) {
    return {
      kind: 'swatch',
      color: object.fill,
      round: object.type === 'circle' || object.type === 'ellipse',
    };
  }
  return { kind: 'icon', icon: 'shape' };
};
